import React, { useEffect, useState } from "react";

import { T } from "services/lang/LanguageService";
import { Button } from "primereact/button";
import { CurrentLineConfig } from "../DatasetLineConfigOptions";
import SelectedDatasetElement from "../SelectedDatasetElement";
import { Axis, AxisAssignedArrays, Dataset, DatasetAxis } from "../models/types";
import "./styles.css";

interface DragAreaProps {
  selectedDatasetsArr: Dataset[];
  removeDataset: any;
  showAxisSelection: boolean;
  assignToAxis: any;
  selectedChartType: string;
  setCurrentLineConfig: (identifier: string, currentLineConfig: CurrentLineConfig) => void;
}

const DragArea: React.FC<DragAreaProps> = ({
  selectedDatasetsArr,
  removeDataset,
  showAxisSelection,
  assignToAxis,
  selectedChartType,
  setCurrentLineConfig,
}) => {
  const [datasetsWithAxis, setDatasetsWithAxis] = useState<DatasetAxis[]>([]);
  const [axisArray, setAxisArray] = useState<AxisAssignedArrays>({ none: [], X: [], Y: [] });
  const [fieldXClassName, setFieldXClassName] = useState("empty");
  const [fieldYClassName, setFieldYClassName] = useState("empty");

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    e.dataTransfer.setData("id", id);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>, axis: Axis) => {
    const id = e.dataTransfer.getData("id");
    const dtXIndex = datasetsWithAxis.findIndex(dta => dta.axis === "X");
    const dts = datasetsWithAxis.map((dt, index) => {
      const newDt = { ...dt };

      if (dt.identifier === id && axis !== "X") {
        newDt.axis = axis;
      }
      if (axis === "X") {
        if (index === dtXIndex) {
          newDt.axis = axis;
        }
        if (dt.identifier === id) {
          newDt.axis = axis;
        }
      }
      return newDt;
    });
    setDatasetsWithAxis(dts);
  };

  useEffect(() => {
    const foundXIndex = datasetsWithAxis.findIndex(d => d.axis === "X");
    const foundYIndex = datasetsWithAxis.findIndex(d => d.axis === "Y");
    setFieldXClassName(foundXIndex !== -1 ? "" : "empty");
    setFieldYClassName(foundYIndex !== -1 ? "" : "empty");
  }, [datasetsWithAxis]);

  useEffect(() => {
    setDatasetsWithAxis(
      selectedDatasetsArr.reduce((res: DatasetAxis[], curr, currentIndex) => {
        if (currentIndex === 0) {
          res.push({ ...curr, axis: "X" });
        } else {
          res.push({ ...curr, axis: "Y" });
        }

        return res;
      }, [])
    );
  }, [selectedDatasetsArr]);

  useEffect(() => {
    setAxisArray({ none: [], X: [], Y: [] });
    const tempArr: AxisAssignedArrays = { none: [], X: [], Y: [] };
    datasetsWithAxis.forEach(d => {
      tempArr[d.axis]?.push(
        <SelectedDatasetElement
          key={d.identifier}
          selectedDataset={d}
          removeDataset={removeDataset}
          dragStart={onDragStart}
          selectedChartType={selectedChartType}
          setCurrentLineConfig={setCurrentLineConfig}
        />
      );
    });
    const xDataset: string | undefined = tempArr.X[0]?.props.selectedDataset.identifier;
    const yDatasets: string[] | undefined = tempArr.Y?.map(y => y.props.selectedDataset.identifier);
    assignToAxis(xDataset || "", yDatasets || []);
    setAxisArray(tempArr);
  }, [datasetsWithAxis, selectedChartType, setCurrentLineConfig, assignToAxis, removeDataset]);

  if (!showAxisSelection) {
    return (
      <div className="padding">
        <div className="flex justify-content-between">
          <label htmlFor="default-drop-box">{T("label.selected-datasets")}: </label>
          {datasetsWithAxis.length > 1 && (
            <Button
              label={T("action.remove-all")}
              className="p-button-text p-0 text-red-300 border-noround"
              icon="pi pi-times"
              onClick={() => removeDataset(datasetsWithAxis.map(d => d.identifier))}
            />
          )}
        </div>
        <div className="drop-box" id="default-drop-box">
          {axisArray.X}
          {axisArray.Y}
          {axisArray.none}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 padding">
      <div>
        <label htmlFor="single-drop-box-x">{T("word.x-axis")}: </label>
        <div
          className={`drop-box ${fieldXClassName}`}
          id="single-drop-box-x"
          onDragOver={onDragOver}
          onDrop={e => {
            onDrop(e, "X");
          }}
        >
          {axisArray.X}
        </div>
      </div>
      <div>
        <label htmlFor="single-drop-box-y">{T("word.y-axis")}: </label>
        <div
          className={`drop-box ${fieldYClassName}`}
          id="single-drop-box-y"
          onDragOver={onDragOver}
          onDrop={e => {
            onDrop(e, "Y");
          }}
        >
          {axisArray.Y}
        </div>
      </div>
    </div>
  );
};

export default DragArea;
